<h1 style="text-align: center;">CodeMath24 - Coding Theory and Discrete Mathematics 2024</h1>
<h2>Organizers</h2>
<p>
    <b></b>
    <em>
        Massimo Giulietti (Università degli Studi di Perugia, Italy)
        <br>
        Giuseppe Marino (Università degli studi di Napoli Federico II, Italy)
        <br>
        Olga Polverino (Università degli Studi della Campania “Luigi Vanvitelli”, Italy)
        <br>
        Ferdinando Zullo (Università degli Studi della Campania “Luigi Vanvitelli”, Italy)
    </em>
</p>
<h2>Description</h2>
<p>
    The aim of the workshop is to create a collaborative environment that promotes research and innovation in these
    fields, to facilitate networking opportunities for participants to engage with peers, industry experts, and
    researchers, and to showcase the practical applications of coding theory and discrete mathematics in areas such as
    error correction and cryptography.
    <br>
    Subjects of interest for the Workshop will be: Algebraic Foundations of Coding theory (Finite fields, Polynomial
    algebras, Algebraic Varieties and their applications in coding), Advanced Topics in Coding Theory (such as turbo
    codes and LDPC codes; network coding and its applications; quantum error correction), Cryptography and its relation
    to Coding theory and Finite Geometry, Current trends and challenges in coding theory and discrete mathematics.
</p>
<h2>Program</h2>
<p>
    <i> September 27th, workshop session</i>
    <br>
    <i> Room C, 11:20 - 13:00</i>
</p>
<p>
    Invited speaker: <b>Gilles Zémor</b>, Institut de Mathématiques de Bordeaux (France)
</p>
<table class="orari" cellspacing="2" cellpadding="2" id="27afternoon">
    <tr bgcolor="lightblue" id="raipqc24">
        <th class="schedule" colspan="2">
            <font size="+1"><b>September 27th (morning) - Workshop Session</b></font>
        </th>
    </tr>
    <tr>
        <td class="schedule">
            11:40<br>12:00
        </td>
        <td>
            <i>
                <b>Gilles Zémor</b>, Institut de Mathématiques de Bordeaux (France)
            </i>
            <br>
            Hadamard products of codes: an Additive Combinatorics perspective
            (<a target="_blank" href="https://drive.google.com/file/d/1zoN0Mlx0QI2EE13E97JmGmmOHUkvsLQS">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                The Hadamard (or Schur) product of two linear codes C and D is the
                linear code generated by all coordinate-wise products of a codeword of
                C with a codeword of D. Code products that have an unusually small
                dimension have found numerous applications in coding theory and beyond:
                they are relevant to algebraic decoding, to cryptanalysis of code-based
                cryptosystems, to secret-sharing for multiparty computation and many
                other topics.
                There is a strong connection between structural results for code
                products of small dimension and theorems of Additive Combinatorics that
                characterise sets of elements of an abelian group with small sumsets.
                We will highlight part of this correspondence, with emphasis on Kneser's
                Addition Theorem, and give some applications.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            12:00<br>12:20
        </td>
        <td>
            <i>
                <b>Daniele Bartoli</b>, Università degli Studi di Perugia (Italy)
            </i>
            <br>
            Recent results on scattered spaces and MRD codes
            <details>
                <summary>
                    Abstract
                </summary>
                Linear sets have numerous applications in various areas of mathematics, in-
                cluding finite geometry and coding theory. In particular, scattered linear sets
                are of special interest due to their unique properties and the role they play
                in these fields. <br>
                One of the most effective methods to describe and analyze these scattered
                linear sets is through the use of linearized polynomials that are particularly
                useful because they allow us to leverage techniques from both algebraic geom-
                etry and group theory. By employing these techniques, one can gain deeper
                insights into the properties of scattered linear sets and their applications.
                In this talk, I will present recent advances in the study of exceptional
                scattered polynomials and their generalizations (scattered sequences).
                Furthermore, I will explore the connection between these concepts and
                maximum rank distance (MRD) codes.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            12:20<br>12:40
        </td>
        <td>
            <i>
                <b>Luca Giuzzi</b>, Università degli Studi di Brescia (Italy)
            </i>
            <br>
            Cryptographic uses of (polar) Grassmannians
            (<a target="_blank" href="https://drive.google.com/file/d/15BRGYOAhwqRA6lIJ8aRsApVwY5wfI2F6">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                Embeddings of (polar) Grassmannians determine error correcting codes with high length and
                nice minimum distance. In this talk we shall propose the use of punctured Grassmann codes in
                a McEliece-like cryptosystem. The security of the scheme is based on hiding from potential
                attackers the point-line structure of the geometry of the Grassmannian, as this structure is
                essential in order to provide efficient decoding algorithms.
                This is a Joint work with Ilaria Cardinali.
            </details>
        </td>
    </tr>
    <tr>
        <td class="schedule">
            12:40<br>13:00
        </td>
        <td>
            <i>
                <b>Violetta Weger</b>, Technical University of Munich (Germany)
            </i>
            <br>
            CROSS: a signature scheme with restricted errors
            (<a target="_blank" href="https://drive.google.com/file/d/1m12hTVEBtYhMrl9o0OiiDietdjgdEgWP">slides</a>)
            <details>
                <summary>
                    Abstract
                </summary>
                The additional call for signature schemes by NIST has shifted the focus of the cryptographic community
                considerably.
                One of the main strategies to construct code-based signatures is through Zero-Knowledge (ZK) protocols.
                This strategy has been deemed impractical for many years due to the large resulting sizes. This size is
                dominated by the linear transitive maps on the set of possible secret keys and by the number of rounds.
                Several proposals to NIST thus change the ZK protocol and employ Multi-Party Computations. This approach
                reduces the number of rounds and in turn the signature sizes. However, this comes at the cost of a
                slower scheme.
                <br>
                CROSS is using a different solution: instead of changing the ZK protocol, we change the underlying
                problem.
                In this talk I will introduce restricted errors, and the Restricted Syndrome Decoding Problem used in
                CROSS. We will explore the mathematical properties of restricted errors and show that they are perfectly
                suited for code-based ZK protocols as they achieve the theoretical minimum in communication costs.
            </details>
        </td>
    </tr>
</table>